/**
 * Pure logic helpers for the Gutachten ↔ Mietwagen cross-link UI (Issue #272).
 *
 * Extracted into a pure module so the state-derivation and URL-building logic
 * can be covered by Vitest without a DOM or React rendering environment.
 */

// Required fields for Mietwagen cross-link (schadendatum omitted — unreliable WinValue sync; versicherung_gegner omitted — not in Akte type).
const FELDER_PFLICHT = ['kunde_name', 'schadennummer'] as const;
type PflichtFeld = (typeof FELDER_PFLICHT)[number];

export function checkVollstaendigkeit(
  akte: Partial<Record<PflichtFeld, string | null | undefined>>,
): { ok: boolean; fehlend: string[] } {
  const fehlend = FELDER_PFLICHT.filter((f) => !akte[f]);
  return { ok: fehlend.length === 0, fehlend };
}

export const FELD_LABELS: Record<PflichtFeld, string> = {
  kunde_name: 'Kundenname',
  schadennummer: 'Schadennummer',
};

export interface MietwagenVorschlag {
  mietwagen_akte_id: string;
  mietwagen_akten_nummer: string | null;
  kunde_name: string | null;
  tagespreis: number | null;
  schadennummer: string | null;
  schadendatum: string | null;
  confidence: 'high' | 'medium';
  match_grund: string;
}

/**
 * The four rendering states of MietwagenCrossLinkCard, derived from props.
 *
 *   linked          — mietwagen_akte_id is set: show linked chip
 *   suggestions     — checkbox active, no link, vorschlaege available: show banner
 *   unlinked_active — checkbox active, no link, no vorschlaege: show action buttons
 *   inactive        — checkbox not active and no link: render nothing
 */
export type MietwagenCrossLinkMode =
  | 'linked'
  | 'suggestions'
  | 'unlinked_active'
  | 'inactive';

export function deriveMietwagenCrossLinkMode(
  mietwagenInAnspruch: boolean | null | undefined,
  mietwagenAkteId: string | null | undefined,
  vorschlaege: MietwagenVorschlag[],
): MietwagenCrossLinkMode {
  if (mietwagenAkteId) return 'linked';
  if (!mietwagenInAnspruch) return 'inactive';
  if (vorschlaege.length > 0) return 'suggestions';
  return 'unlinked_active';
}

export interface MietwagenNeuanlagePrefill {
  kundeName: string | null | undefined;
  schadennummer: string | null | undefined;
  schadendatum: string | null | undefined;
}

/**
 * Builds the URL for the mietwagen AktenPage with query params that trigger
 * the NeueAkteModal to pre-open with prefill values from the Gutachten-Akte.
 *
 * AktenPage reads these params on mount:
 *   ?neue-akte=1&kunde=...&schadennummer=...&schadendatum=...
 */
export function buildMietwagenNeuanlageUrl(prefill: MietwagenNeuanlagePrefill): string {
  const params = new URLSearchParams();
  params.set('neue-akte', '1');
  if (prefill.kundeName) params.set('kunde', prefill.kundeName);
  if (prefill.schadennummer) params.set('schadennummer', prefill.schadennummer);
  if (prefill.schadendatum) params.set('schadendatum', prefill.schadendatum);
  return `/akten/mietwagen?${params.toString()}`;
}
